/* eslint-disable react-refresh/only-export-components --
   REQ-00 §13 legt fest, dass DialogProvider und useDialogService in dieser Datei liegen. */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { Button } from './Button';
import { Dialog } from './Dialog';

/** Pendant zu `SfDialogService.AlertAsync`/`ConfirmAsync`. */
export interface DialogService {
  alert: (content: ReactNode, title?: string) => Promise<void>;
  confirm: (content: ReactNode, title?: string) => Promise<boolean>;
}

interface DialogRequest {
  key: number;
  kind: 'alert' | 'confirm';
  content: ReactNode;
  title?: string;
}

const DialogServiceContext = createContext<DialogService | null>(null);

export function DialogProvider(props: { children: ReactNode }) {
  // Mehrere gleichzeitige Aufrufe werden als Stapel gehalten (REQ-06 §3).
  const [requests, setRequests] = useState<DialogRequest[]>([]);
  const resolvers = useRef(new Map<number, (result: boolean) => void>());
  const nextKey = useRef(0);

  const settle = useCallback((key: number, result: boolean) => {
    const resolve = resolvers.current.get(key);
    resolvers.current.delete(key);
    setRequests((current) => current.filter((request) => request.key !== key));
    resolve?.(result);
  }, []);

  const push = useCallback(
    (kind: DialogRequest['kind'], content: ReactNode, title: string | undefined) =>
      new Promise<boolean>((resolve) => {
        nextKey.current += 1;
        const key = nextKey.current;
        resolvers.current.set(key, resolve);
        setRequests((current) => [...current, { key, kind, content, title }]);
      }),
    [],
  );

  const service = useMemo<DialogService>(
    () => ({
      alert: async (content, title) => {
        await push('alert', content, title);
      },
      confirm: (content, title) => push('confirm', content, title),
    }),
    [push],
  );

  return (
    <DialogServiceContext.Provider value={service}>
      {props.children}
      {requests.map((request) => (
        <Dialog
          key={request.key}
          open
          header={request.title}
          onClose={() => {
            settle(request.key, false);
          }}
          footer={
            <>
              {request.kind === 'confirm' ? (
                <Button
                  content="Cancel"
                  onClick={() => {
                    settle(request.key, false);
                  }}
                />
              ) : null}
              <Button
                primary
                content="OK"
                onClick={() => {
                  settle(request.key, true);
                }}
              />
            </>
          }
        >
          {request.content}
        </Dialog>
      ))}
    </DialogServiceContext.Provider>
  );
}

export function useDialogService(): DialogService {
  const service = useContext(DialogServiceContext);
  if (service === null) throw new Error('useDialogService erfordert einen DialogProvider.');
  return service;
}

export default DialogProvider;
